<script>
import { EzConfirmationModal } from '@/components/ui/Modal';
import EzButton from '@/components/ui/Button/EzButton';
import EzCheckbox from '@/components/ui/Checkbox/Checkbox';
import VSelectSearch from '@/components/v3/patterns/VSelectSearch';
import { camalize } from '@/util/utils';

/**
 *
 * @version 1.0.0
 * @since
 */
export default {
  name: 'UpdateModal',
  components: {
    EzConfirmationModal,
    EzButton,
    EzCheckbox,
    VSelectSearch,
  },
  props: {
    entity: {
      type: String,
      required: false,
      default: 'Products',
    },
    categories: {
      type: Array,
      required: true,
      default: () => [],
    },
    accData: {
      type: Object,
      required: true,
      default: () => {},
    },
    taxes: {
      type: Array,
      required: false,
      default: () => [],
    },
    showTaxRate: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      formKey: 'updateModalXero',
      categoriesCopy: [],
      isAccChecked: false,
      isInventoryAccChecked: false,
      selectedCategories: {},
      config: {},
      disabled: true,
      accountCode: '',
      inventoryAccountCode: '',
      isTaxTypeChecked: false,
      taxType: '',
    };
  },
  computed: {
    mappedCategories() {
      return this.categoriesCopy;
    },
    accountDisabled() {
      return !this.isAccChecked || (this.isAccChecked && this.accountCode === '');
    },
    inventoryAccountDisabled() {
      return (
        !this.isInventoryAccChecked ||
        (this.isInventoryAccChecked && this.inventoryAccountCode === '')
      );
    },
    restDisabled() {
      return this.disabled;
    },
    taxTypeDisabled() {
      return !this.isTaxTypeChecked || (this.isTaxTypeChecked && this.taxType === '');
    },
    isDisabled() {
      return (
        this.accountDisabled &&
        this.restDisabled &&
        this.taxTypeDisabled &&
        this.inventoryAccountDisabled
      );
    },
    accounts() {
      return this.accData?.accounts;
    },
  },
  methods: {
    onClose() {
      this.mappedCategories.forEach(cat => {
        const comp = this.$refs[cat.ref];
        comp[0].reset();
        cat.checked = false;
      });
      this.isAccChecked = false;
      this.isInventoryAccChecked = false;
      this.$refs.accDropdown.reset();
      this.$refs.inventoryAccDropdown.reset();
      this.accountCode = '';
      this.inventoryAccountCode = '';
      this.disabled = true;
      this.config = {};
      this.selectedCategories = {};
      this.isTaxTypeChecked = false;
      this.taxType = '';
    },
    open() {
      this.$refs.updateModal.open();
    },
    close() {
      this.$refs.updateModal.close();
    },
    onCheckboxChange(cat, ev) {
      const category = this.categoriesCopy.find(
        ct => ct.trackingCategoryID === cat.trackingCategoryID,
      );
      category.checked = ev;
      if (!ev) {
        const comp = this.$refs[category.ref];
        comp[0].reset();
      }
      this.onDropdownChange(category, null);
    },
    onTaxTypeCheck(val) {
      this.isTaxTypeChecked = val;
      this.taxType = '';
    },
    onDropdownChange(cat, ev) {
      this.selectedCategories = {
        ...this.selectedCategories,
        [cat.name]: ev?.name || null,
      };

      const reducedObj = Object.keys(this.selectedCategories).reduce((newObj, key) => {
        const value = this.selectedCategories[key];
        if (value) {
          newObj[key] = value;
        }
        return newObj;
      }, {});

      this.config = {
        ...this.config,
        tracking: {
          ...reducedObj,
        },
      };
      const equal =
        this.categoriesCopy.filter(ct => ct.checked)?.length ===
        Object.values(this.config.tracking)?.length;
      const checkBoxLength = this.categoriesCopy.filter(ct => ct.checked)?.length === 0;
      this.disabled = !equal || checkBoxLength;
    },
    toggleAcc(ev) {
      this.isAccChecked = ev;
      if (!ev) {
        this.$refs.accDropdown.reset();
        this.accountCode = '';
        delete this.config.accountCode;
      }
    },
    accChanged(ev) {
      this.accountCode = ev.code;
      this.config = {
        ...this.config,
        accountCode: ev.code,
      };
    },
    toggleInventoryAcc(ev) {
      this.isInventoryAccChecked = ev;
      if (!ev) {
        this.$refs.inventoryAccDropdown.reset();
        this.inventoryAccountCode = '';
        delete this.config.venueInventoryAccountCode;
      }
    },
    inventoryAccChanged(ev) {
      this.inventoryAccountCode = ev.code;
      this.config = {
        ...this.config,
        venueInventoryAccountCode: ev.code,
      };
    },
    update() {
      this.$emit('update', this.config);
    },
  },
  created() {
    this.categoriesCopy = this.categories.map(cat => ({
      ...cat,
      checked: false,
      ref: camalize(cat.trackingCategoryID),
    }));
  },
  watch: {
    categories() {
      this.categoriesCopy = this.categories.map(cat => ({
        ...cat,
        checked: false,
        ref: camalize(cat.trackingCategoryID),
      }));
    },
    taxType(val) {
      if (val === '') delete this.config.taxType;
      else this.config = { ...this.config, taxType: val };
    },
  },
};
</script>

<template>
  <ez-confirmation-modal @close="onClose" ref="updateModal" icon="question">
    <template #title>Update {{ entity }}?</template>
    <template #content>
      <p>Choose what information you would like to change for selected {{ entity }}.</p>
      <hr />
      <ez-checkbox
        label="Account"
        class="cursor-pointer mb-12"
        :checked="isAccChecked"
        @change="toggleAcc"
      />
      <v-select-search
        ref="accDropdown"
        :disable="!isAccChecked"
        class="mb-12 width-100"
        align-left
        is-full-width
        :formKey="formKey"
        name="account"
        :data="accounts"
        value-property="code"
        placeholder="Select Account"
        searchPlaceholder="Search Account"
        :has-clear="false"
        @selected="accChanged"
      >
        <template #result="{ result }">
          <span>
            {{ result.name }}
          </span>
        </template>
      </v-select-search>
      <ez-checkbox
        label="Inventory Account"
        class="cursor-pointer mb-12"
        :checked="isInventoryAccChecked"
        @change="toggleInventoryAcc"
      />
      <v-select-search
        ref="inventoryAccDropdown"
        :disable="!isInventoryAccChecked"
        class="mb-12 width-100"
        align-left
        is-full-width
        :formKey="formKey"
        name="inventoryAccount"
        :data="accounts"
        value-property="code"
        placeholder="Select Inventory Account"
        searchPlaceholder="Search Inventory Account"
        :has-clear="false"
        @selected="inventoryAccChanged"
      >
        <template #result="{ result }">
          <span>
            {{ result.name }}
          </span>
        </template>
      </v-select-search>
      <template v-if="showTaxRate">
        <hr />
        <ez-checkbox
          label="Tax Rate"
          :checked="isTaxTypeChecked"
          @change="onTaxTypeCheck"
          class="cursor-pointer mb-8 height-auto"
        />
        <v-select-search
          ref="taxRateDropdown"
          :disable="!isTaxTypeChecked"
          class="width-100 mb-12"
          align-left
          is-full-width
          name="taxType"
          :data="taxes"
          value-property="taxType"
          placeholder="Select Tax Rate"
          searchPlaceholder="Search Tax Rate"
          :has-clear="false"
          @selected="taxType = $event.taxType"
        >
          <template #result="{ result }">
            <span>
              {{ result.name }}
            </span>
          </template>
        </v-select-search>
      </template>
      <hr v-if="mappedCategories.length" />
      <p class="cat-label" v-if="mappedCategories.length">Tracking Categories</p>
      <div v-for="cat in mappedCategories" :key="cat.trackingCategoryID">
        <ez-checkbox
          :label="cat.name"
          class="cursor-pointer mb-12"
          :checked="cat.checked"
          @change="onCheckboxChange(cat, $event)"
        />
        <v-select-search
          v-if="cat.options.length"
          :ref="cat.ref"
          :disable="!cat.checked"
          class="width-100 mb-12"
          align-left
          is-full-width
          name="account"
          :data="cat.options"
          :formKey="formKey"
          value-property="value"
          :placeholder="`Select ${cat.name}`"
          :searchPlaceholder="`Search ${cat.name}`"
          :has-clear="false"
          @selected="onDropdownChange(cat, $event)"
        >
          <template #result="{ result }">
            <span>
              {{ result.name }}
            </span>
          </template>
        </v-select-search>
      </div>
    </template>
    <template #footer>
      <ez-button @click="close" type="link">Cancel</ez-button>
      <ez-button :disabled="isDisabled" @click="update">Update {{ entity }}</ez-button>
    </template>
  </ez-confirmation-modal>
</template>

<style lang="scss" scoped>
.cat-label {
  @include font-size(14px, 22px);
  color: $color-gray-25;
}

:deep() .select-search__trigger {
  span {
    color: $color-gray-6C;
  }
  svg {
    color: $color-gray-6C;
  }
  span.select-search__value {
    color: $color-gray-25;
  }
}

:deep() .select-search--disabled .select-search__trigger {
  span,
  svg {
    color: $input-disabled-color;
  }
}

:deep() .select-search__list li.select-search__item {
  padding: 0.625rem 1rem;
}
</style>
